import type { PreviewWithGitMetadata, PreviewDisplayData, DeployedAppInfo } from "../types/preview-environments";
import { NAMING_PATTERN_MAX_LENGTH, PREVIEW_TEMPLATE_VARS } from "./preview-constants";
import { getElasticSearchService } from "../backend";

// ─── getPreviewDisplay ───────────────────────────────────────────────────────

export function getPreviewDisplay(preview: PreviewWithGitMetadata): PreviewDisplayData {
    const meta = preview.gitMetadata;
    if (meta?.type === "TAG") {
        return {
            label: meta.tagName,
            title: meta.tagName,
            url: undefined,
            branch: meta.baseBranch || meta.branch || "",
            headSha: meta.headSha,
            author: meta.author,
            isPR: false,
            changedFiles: meta.changedFiles,
        };
    }
    const prMeta = meta || (preview as any);
    return {
        label: `#${prMeta.prNumber}`,
        title: prMeta.prTitle || "",
        url: prMeta.prUrl,
        branch: prMeta.branch || "",
        baseBranch: prMeta.baseBranch,
        headSha: prMeta.headSha || "",
        author: prMeta.author || "",
        isPR: true,
        additions: prMeta.additions,
        deletions: prMeta.deletions,
        changedFiles: prMeta.changedFiles,
    };
}

// ─── K8s namespace helpers ───────────────────────────────────────────────────

/** Mirrors backend sanitizeK8sNamespace from preview-environment-manager.ts */
export function sanitizeK8sNamespace(raw: string): string {
    return raw
        .toLowerCase()
        .replace(/\{[^}]*\}/g, "")
        .replace(/[^a-z0-9-]/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "")
        .substring(0, 63);
}

export function validateNamespacePattern(pattern: string): string | undefined {
    if (!pattern || !pattern.trim()) {
        return "Namespace pattern is required";
    }
    if (pattern.length > NAMING_PATTERN_MAX_LENGTH) {
        return `Pattern must be ${NAMING_PATTERN_MAX_LENGTH} characters or less`;
    }
    const validVars: readonly string[] = PREVIEW_TEMPLATE_VARS;
    const staticPart = validVars.reduce((p, v) => p.split(v).join(""), pattern);
    if (!/^[a-z0-9-]+$/.test(staticPart)) {
        return "Only lowercase letters, numbers, and hyphens are allowed (besides template variables like {MIC_PR_NUMBER})";
    }
    return undefined;
}

export function resolvePatternPreview(pattern: string, forTag?: boolean): string {
    return pattern
        .replace(/\{MIC_PR_NUMBER\}/g, forTag ? "" : "42")
        .replace(/\{MIC_BRANCH\}/g, forTag ? "v1.0" : "feature-foo")
        .replace(/\{MIC_TAG_NAME\}/g, forTag ? "v1.0" : "")
        .replace(/\{MIC_SHORT_SHA\}/g, "a1b2c3d")
        .replace(/\{MIC_AUTHOR\}/g, "dev");
}

// ─── Fetch deployed apps ─────────────────────────────────────────────────────

export async function fetchDeployedApps(
    projectId: string,
    envId: string,
    appNames?: string[]
): Promise<DeployedAppInfo[]> {
    const { data: { response: appDeployments } } = await getElasticSearchService()
        .getDeployedMicroservices(projectId, undefined, undefined, envId, undefined, undefined, appNames);

    const appMap = new Map<string, DeployedAppInfo>();
    for (const deployment of (appDeployments || [])) {
        if (deployment.name && !appMap.has(deployment.name)) {
            appMap.set(deployment.name, {
                name: deployment.name,
                pipelineId: (deployment.commit as any)?.pipelineId || undefined,
            });
        }
    }

    return Array.from(appMap.values());
}

// ─── Clipboard helper ────────────────────────────────────────────────────────

export async function safeClipboardWrite(text: string): Promise<boolean> {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        return false;
    }
}

// ─── JSON parse helper ───────────────────────────────────────────────────────

export function safeJsonParse<T>(text: string): T | undefined {
    try {
        return JSON.parse(text) as T;
    } catch {
        return undefined;
    }
}
